import React, { useState } from "react";
import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import useApiEp from "../hooks/useApiEp";
import ToolsLayout, { RefreshTools, ToolsState } from "./ToolsLayout";
import { money, stockState, toolsDate } from "./toolsData";
import StockDialog from "./StockDialog";
import FullImageModal from "../widgets/FullImageModal";

export default function ViewProduct() {
  const { id } = useParams();
  const query = useApiEp(`admin/tool/${encodeURIComponent(id)}`);
  const [stockOpen, setStockOpen] = useState(false),
    [preview, setPreview] = useState(""),
    [notice, setNotice] = useState("");
  const product = query.data;
  const state = stockState(product?.stock);
  return (
    <ToolsLayout
      title={product?.name || "Product details"}
      description="Review the customer-facing product details and current inventory."
      actions={
        <>
          <RefreshTools onClick={query.refetch} loading={query.isFetching} />
          <Button
            variant="contained"
            component={Link}
            to={`/home/${encodeURIComponent(id)}/edit-tool`}
          >
            Edit product
          </Button>
        </>
      }
    >
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      <ToolsState
        loading={query.isLoading}
        error={query.isError}
        retry={query.refetch}
        empty={!product}
        emptyTitle="Product not found"
        emptyMessage="The product may have been removed or the reference is invalid."
      >
        {product && (
          <div className="tools-two-column">
            <section className="tools-panel">
              <div className="tools-panel-heading">
                <Typography component="h2" variant="h6">
                  Product images
                </Typography>
              </div>
              <Box
                sx={{
                  p: 3,
                  pt: 0,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 2,
                }}
              >
                {(product.images || (product.imageUrl ? [product.imageUrl] : [])).map(
                  (url, index) => (
                    <Box
                      component="button"
                      type="button"
                      aria-label={`Enlarge product image ${index + 1}`}
                      key={url}
                      onClick={() => setPreview(url)}
                      sx={{
                        p: 1,
                        bgcolor: "background.default",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        cursor: "zoom-in",
                      }}
                    >
                      <img
                        src={url}
                        alt={`${product.name}, view ${index + 1}`}
                        style={{ width: "100%", height: 230, objectFit: "contain" }}
                      />
                    </Box>
                  ),
                )}
              </Box>
              {!product.images?.length && !product.imageUrl && (
                <Typography sx={{ px: 3, pb: 3 }} color="text.secondary">
                  No product images available.
                </Typography>
              )}
            </section>
            <Stack spacing={3}>
              <section className="tools-panel">
                <div className="tools-panel-heading">
                  <Typography component="h2" variant="h6">
                    Pricing & stock
                  </Typography>
                </div>
                <Box sx={{ px: 3, pb: 3 }}>
                  <Typography variant="h4">{money(product.sellingPrice)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    MRP {money(product.mrp)} · GST {product.gst ?? "—"}%
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      state === "unknown"
                        ? "Stock unavailable"
                        : state === "out"
                          ? "Out of stock"
                          : `${product.stock} units · ${state === "low" ? "Low stock" : "In stock"}`
                    }
                    color={state === "low" ? "warning" : state === "out" ? "error" : "default"}
                  />
                  <Button
                    sx={{ display: "block", mt: 2 }}
                    variant="outlined"
                    disabled={state === "unknown"}
                    onClick={() => setStockOpen(true)}
                  >
                    Update stock
                  </Button>
                </Box>
              </section>
              <section className="tools-panel">
                <div className="tools-panel-heading">
                  <Typography component="h2" variant="h6">
                    Product information
                  </Typography>
                </div>
                <Box sx={{ px: 3, pb: 3 }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-line", mb: 3 }}>
                    {product.description || "No description available."}
                  </Typography>
                  <Typography variant="body2">
                    Category: {product.category || "Uncategorised"}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Unit weight:{" "}
                    {product.weight
                      ? `${product.weight} ${product.weightUnit || "kg"}`
                      : "Not specified"}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                  >
                    Created {toolsDate(product.createdOn)} · Updated {toolsDate(product.updatedOn)}
                  </Typography>
                </Box>
              </section>
            </Stack>
          </div>
        )}
      </ToolsState>
      {stockOpen && product && (
        <StockDialog product={product} onClose={() => setStockOpen(false)} onSaved={setNotice} />
      )}
      <FullImageModal open={Boolean(preview)} handleClose={() => setPreview("")} imgUrl={preview} />
    </ToolsLayout>
  );
}
